import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AddUsersToRole_Payload_Type,
  CreateRole_Payload_Type,
  addUsersToRole,
  createRole,
  deleteRole,
  getRolesByCompany,
  getUsersByRole,
  removeUserFromRole,
  GetRoles_Response_Type,
} from '../axiosNetworkRequest/roles';
import { useSelector } from 'react-redux';
import { ReduxRootStateType } from '../../redux/store';
import { track } from '../../utils/MixpanelHelper';
import useNotification from '../../hooks/useNotification';
import { API_Error_Type } from '../axiosClient/axiosClient';

export const useGetRolesByCompany = (company_id: number) =>
  useQuery({
    queryKey: ['roles', company_id],
    queryFn: () => getRolesByCompany(company_id),
    enabled: company_id > 0,
  });

export const useCreateRole = (company_id: number) => {
  const qc = useQueryClient();
  const userInfo = useSelector((state: ReduxRootStateType) => state.user.user_info);
  const { setMessage, setSeverity, handleSnackbarOpen } = useNotification();
  return useMutation({
    mutationFn: (payload: CreateRole_Payload_Type) => createRole(payload),
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: ['roles', company_id] });
      track('Role Created', {
        'Role Name':    variables.role_name,
        'Created By':   `${userInfo?.first_name} ${userInfo?.last_name}`,
        'Email':        userInfo?.email_id,
        'Company Name': userInfo?.company_name,
      });
    },
    onError: (error) => {
      const err = error as unknown as API_Error_Type;
      setMessage(err.client_error_msg ?? 'Failed to create role');
      setSeverity('error');
      handleSnackbarOpen();
    },
  });
};

export const useDeleteRole = (company_id: number) => {
  const qc = useQueryClient();
  const userInfo = useSelector((state: ReduxRootStateType) => state.user.user_info);
  const { setMessage, setSeverity, handleSnackbarOpen } = useNotification();
  return useMutation({
    mutationFn: (role_id: number) => deleteRole(role_id),
    onSuccess: (_, role_id) => {
      const cached = qc.getQueryData<GetRoles_Response_Type>(['roles', company_id]);
      const role   = cached?.data.find(r => r.role_id === role_id);
      qc.invalidateQueries({ queryKey: ['roles', company_id] });
      track('Role Deleted', {
        'Role Name':    role?.role_name ?? 'unknown',
        'Deleted By':   `${userInfo?.first_name} ${userInfo?.last_name}`,
        'Email':        userInfo?.email_id,
        'Company Name': userInfo?.company_name,
      });
    },
    onError: (error) => {
      const err = error as unknown as API_Error_Type;
      setMessage(err.client_error_msg ?? 'Failed to delete role');
      setSeverity('error');
      handleSnackbarOpen();
    },
  });
};

export const useGetUsersByRole = (role_id: number | undefined) =>
  useQuery({
    queryKey: ['role-users', role_id],
    queryFn: () => getUsersByRole(role_id!),
    enabled: role_id !== undefined && role_id > 0,
  });

export const useAddUserToRole = () => {
  const qc = useQueryClient();
  const { setMessage, setSeverity, handleSnackbarOpen } = useNotification();
  return useMutation({
    mutationFn: ({ role_id, payload }: { role_id: number; payload: AddUsersToRole_Payload_Type }) =>
      addUsersToRole(role_id, payload),
    onSuccess: (_, { role_id }) => qc.invalidateQueries({ queryKey: ['role-users', role_id] }),
    onError: (error) => {
      const err = error as unknown as API_Error_Type;
      setMessage(err.client_error_msg ?? 'Failed to add user to role');
      setSeverity('error');
      handleSnackbarOpen();
    },
  });
};

export const useRemoveUserFromRole = () => {
  const qc = useQueryClient();
  const { setMessage, setSeverity, handleSnackbarOpen } = useNotification();
  return useMutation({
    mutationFn: ({ role_id, user_role_id }: { role_id: number; user_role_id: number }) =>
      removeUserFromRole(role_id, user_role_id),
    onSuccess: (_, { role_id }) => qc.invalidateQueries({ queryKey: ['role-users', role_id] }),
    onError: (error) => {
      const err = error as unknown as API_Error_Type;
      setMessage(err.client_error_msg ?? 'Failed to remove user from role');
      setSeverity('error');
      handleSnackbarOpen();
    },
  });
};
